// Single data source for the Executive Cockpit.
//
// Fans out across the KPI-relevant Laravel admin endpoints (no API changes — just
// fetchTable with limit:200) and returns structured business data + computed KPIs.
// Pattern lifted from ReportsPage / ResourceHealthPage. All KPIs are client-computed
// (the endpoints have no server-side aggregation), so sums are bounded by the page
// size — KPIs with the `hint: 'recent'` flag reflect recent activity only.

import { useEffect, useState } from 'react';
import { fetchBusinessInsights, fetchDashboard, fetchTable } from '../api/client';
import { ensureLocation, onLocationChanged } from '../session/location';
import { CrudRecord } from '../crud/types';
import type { DashboardResponse } from '../types';
import {
  cashPosition,
  inventoryValue,
  Kpi,
  lowStockItems,
  lowStockKpi,
  openCashRegisters,
  ordersWaiting,
  payables,
  receivables,
  revenueThisMonth,
  revenueToday
} from './kpis';
import { buildBusinessSnapshot, buildInsights, BusinessMessage } from './insights';
import { t } from '../i18n';

export type BusinessData = {
  loading: boolean;
  error: string | null;
  rows: {
    sales: CrudRecord[];
    purchases: CrudRecord[];
    items: CrudRecord[];
    accounts: CrudRecord[];
    customers: CrudRecord[];
    suppliers: CrudRecord[];
    orders: CrudRecord[];
    returnOrders: CrudRecord[];
    registerRecords: CrudRecord[];
    payments: CrudRecord[];
    incomes: CrudRecord[];
    expenses: CrudRecord[];
  };
  counts: {
    orders: number;
    sales: number;
    purchases: number;
    items: number;
    customers: number;
    returnOrders: number;
  };
  charts: {
    salesByRegister: PieDatum[];
    salesByCashier: PieDatum[];
    salesByPaymentType: PieDatum[];
    salesByItem: PieDatum[];
    salesByItemCategory: PieDatum[];
    returnsByItem: PieDatum[];
    salesByHourLastSevenDays: HourlySalesChartData;
  };
  dashboard: DashboardResponse | null;
  kpis: {
    revenueToday: Kpi;
    revenueMonth: Kpi;
    cash: Kpi;
    inventory: Kpi;
    receivables: Kpi;
    payables: Kpi;
    openCashRegisters: Kpi;
    ordersWaiting: Kpi;
    lowStock: Kpi;
  };
  alerts: BusinessMessage[];
  snapshot: ReturnType<typeof buildBusinessSnapshot> | null;
  refreshedAt: Date | null;
};

export type PieDatum = {
  label: string;
  value: number;
};

export type HourlySalesChartData = {
  categories: string[];
  series: Array<{ name: string; data: number[] }>;
};

async function fetchRows<T extends CrudRecord>(resource: string, orderBy: string, extra?: Record<string, string>): Promise<{ rows: T[]; count: number }> {
  try {
    const table = await fetchTable<T>(resource, { page: 1, limit: 200, orderBy, ...(extra ? { extra } : {}) });
    return { rows: table.data ?? [], count: table.count ?? 0 };
  } catch {
    return { rows: [], count: 0 };
  }
}

function relationLabel(value: unknown, fallback = 'Unknown') {
  if (!value || typeof value !== 'object') return fallback;
  const record = value as Record<string, unknown>;
  return String(record.name ?? record.label ?? record.title ?? record.username ?? record.code ?? fallback);
}

function addBucket(map: Map<string, number>, label: string, amount: number) {
  const cleanLabel = label.trim() || 'Unknown';
  map.set(cleanLabel, (map.get(cleanLabel) ?? 0) + (Number.isFinite(amount) ? amount : 0));
}

function normalizeServerMessages(value: unknown): BusinessMessage[] {
  if (!Array.isArray(value)) return [];
  const severityMap: Record<string, BusinessMessage['severity']> = { INFO: 'info', SUCCESS: 'success', WARNING: 'warning', CRITICAL: 'error' };
  return value.filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object')).map((entry) => translateBusinessMessage({
    code: String(entry.code ?? ''),
    type: entry.type === 'alert' ? 'alert' : 'insight',
    severity: severityMap[String(entry.severity ?? 'INFO').toUpperCase()] ?? 'info',
    priority: Number(entry.priority ?? 0),
    title: String(entry.title ?? ''),
    description: String(entry.description ?? ''),
    detail: String(entry.description ?? ''),
    metricValues: (entry.metrics && typeof entry.metrics === 'object' ? entry.metrics : {}) as Record<string, number | string>,
    action: String(entry.action ?? ''),
    timestamp: String(entry.timestamp ?? new Date().toISOString()),
    to: String(entry.to ?? 'reports')
  }));
}

function translateBusinessMessage(message: BusinessMessage): BusinessMessage {
  const metrics = message.metricValues;
  const numberValue = (...keys: string[]) => keys.map((key) => Number(metrics[key])).find((value) => Number.isFinite(value)) ?? 0;
  const values = {
    change: Math.abs(numberValue('changePercent', 'change_percent')).toFixed(1),
    refunds: numberValue('refundRate', 'refund_rate').toFixed(1),
    difference: numberValue('cashDifference', 'cash_difference').toFixed(2),
    count: numberValue('count'),
    product: String(metrics.product ?? metrics.name ?? ''),
    quantity: numberValue('quantity'),
    hour: String(numberValue('hour')).padStart(2, '0'),
    nextHour: String((numberValue('hour') + 1) % 24).padStart(2, '0')
  };
  const key = `insight.${message.code}`;
  return {
    ...message,
    title: t(`${key}.title`),
    description: t(`${key}.description`, values),
    detail: t(`${key}.description`, values),
    action: t(`${key}.action`)
  };
}

function toPieData(map: Map<string, number>, limit = 8): PieDatum[] {
  const rows = Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  if (rows.length <= limit) return rows;
  const head = rows.slice(0, limit - 1);
  const other = rows.slice(limit - 1).reduce((total, row) => total + row.value, 0);
  return [...head, { label: 'Other', value: other }];
}

function saleAmount(row: CrudRecord) {
  return Number(row.grand_total ?? row.total ?? 0);
}

function lineAmount(line: Record<string, unknown>) {
  const subtotal = Number(line.subtotal ?? 0);
  if (subtotal > 0) return subtotal;
  const unit = Number(line.price ?? line.unit_price ?? 0);
  return unit * Number(line.quantity ?? 0);
}

function itemLabel(line: Record<string, unknown>) {
  return String(line.name ?? line.code ?? line.item_id ?? 'Unknown item');
}

function itemCategoryLabel(line: Record<string, unknown>) {
  const item = line.item && typeof line.item === 'object' ? (line.item as Record<string, unknown>) : null;
  const categories = Array.isArray(item?.categories) ? item.categories : [];
  const category = categories.find((entry) => entry && typeof entry === 'object') as Record<string, unknown> | undefined;
  return String(category?.name ?? category?.label ?? category?.title ?? 'Uncategorized');
}

function buildSalesByRegister(sales: CrudRecord[]) {
  const buckets = new Map<string, number>();
  sales.filter((sale) => !sale.draft).forEach((sale) => addBucket(buckets, relationLabel(sale.register, sale.register_id ? `Register ${sale.register_id}` : 'No register'), saleAmount(sale)));
  return toPieData(buckets);
}

function buildSalesByCashier(sales: CrudRecord[]) {
  const buckets = new Map<string, number>();
  sales.filter((sale) => !sale.draft).forEach((sale) => addBucket(buckets, relationLabel(sale.user, sale.user_id ? `Cashier ${sale.user_id}` : 'No cashier'), saleAmount(sale)));
  return toPieData(buckets);
}

function buildSalesByPaymentType(sales: CrudRecord[]) {
  const buckets = new Map<string, number>();
  sales.filter((sale) => !sale.draft).forEach((sale) => {
    const label = String(sale.payment_gateway ?? sale.gateway ?? sale.payment_method ?? (Number(sale.paid) ? 'Paid' : 'Unpaid'));
    addBucket(buckets, label, saleAmount(sale));
  });
  return toPieData(buckets);
}

function buildItemsPie(rows: CrudRecord[]) {
  const buckets = new Map<string, number>();
  rows.forEach((row) => {
    if (row.draft) return;
    const items = Array.isArray(row.items) ? row.items : [];
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const line = item as Record<string, unknown>;
      addBucket(buckets, itemLabel(line), lineAmount(line));
    });
  });
  return toPieData(buckets);
}

function buildSalesByItemCategory(sales: CrudRecord[]) {
  const buckets = new Map<string, number>();
  sales.forEach((sale) => {
    if (sale.draft) return;
    const items = Array.isArray(sale.items) ? sale.items : [];
    items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const line = item as Record<string, unknown>;
      addBucket(buckets, itemCategoryLabel(line), lineAmount(line));
    });
  });
  return toPieData(buckets);
}

function dayKey(date: Date) {
  return date.toISOString().slice(0, 10);
}

function dayShortLabel(dateKey: string) {
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function buildSalesByHourLastSevenDays(sales: CrudRecord[]): HourlySalesChartData {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_value, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (6 - index));
    return dayKey(date);
  });
  const buckets = new Map<string, number[]>();
  days.forEach((key) => buckets.set(key, Array.from({ length: 24 }, () => 0)));

  sales.forEach((sale) => {
    if (sale.draft) return;
    const raw = sale.created_at ?? sale.date;
    if (!raw) return;
    const date = new Date(String(raw));
    if (Number.isNaN(date.getTime())) return;
    const key = dayKey(date);
    const bucket = buckets.get(key);
    if (!bucket) return;
    bucket[date.getHours()] += saleAmount(sale);
  });

  return {
    categories: Array.from({ length: 24 }, (_value, hour) => `${String(hour).padStart(2, '0')}:00`),
    series: days.map((key) => ({ name: dayShortLabel(key), data: buckets.get(key) ?? [] }))
  };
}

export function buildAlerts(data: Pick<BusinessData, 'rows'>) {
  const alerts: Array<{ severity: 'error' | 'warning' | 'info'; title: string; detail: string; to: string }> = [];
  const low = lowStockItems(data.rows.items);
  if (low.length) {
    alerts.push({
      severity: 'error',
      title: `${low.length} product${low.length === 1 ? '' : 's'} low on stock`,
      detail: low.slice(0, 3).map((i) => i.name).join(', ') + (low.length > 3 ? ` +${low.length - 3} more` : ''),
      to: 'items'
    });
  }
  // 'paid' is a boolean flag (tinyint 0/1), not a monetary amount. A sale is
  // outstanding when it's finalized (draft=0) and unpaid (paid=0).
  const unpaidSales = data.rows.sales.filter((s) => !s.draft && Number(s.paid) === 0);
  if (unpaidSales.length) {
    const total = unpaidSales.reduce((a, s) => a + Number(s.grand_total), 0);
    alerts.push({
      severity: 'warning',
      title: `${unpaidSales.length} unpaid sale${unpaidSales.length === 1 ? '' : 's'}`,
      detail: `${Math.round(total).toLocaleString()} outstanding`,
      to: 'sales'
    });
  }
  const unpaidPurchases = data.rows.purchases.filter((p) => !p.draft && Number(p.paid) === 0);
  if (unpaidPurchases.length) {
    const total = unpaidPurchases.reduce((a, p) => a + Number(p.grand_total), 0);
    alerts.push({
      severity: 'warning',
      title: `${unpaidPurchases.length} unpaid purchase${unpaidPurchases.length === 1 ? '' : 's'}`,
      detail: `${Math.round(total).toLocaleString()} owed to suppliers`,
      to: 'purchases'
    });
  }
  return alerts;
}

export function useBusinessData(autoRefresh = false): BusinessData & { refresh: () => void } {
  const [data, setData] = useState<BusinessData | null>(null);

  async function load() {
    const [sales, purchases, items, accounts, customers, suppliers, orders, returnOrders, registerRecords, payments, incomes, expenses, dashboard, serverInsights] =
      await Promise.all([
        fetchRows<CrudRecord>('sales', 'created_at desc', { with_items: '1' }),
        fetchRows<CrudRecord>('purchases', 'date desc'),
        fetchRows<CrudRecord>('items', 'code asc'),
        fetchRows<CrudRecord>('accounts', 'name asc'),
        fetchRows<CrudRecord>('customers', 'name asc'),
        fetchRows<CrudRecord>('suppliers', 'name asc'),
        fetchRows<CrudRecord>('orders', 'date desc'),
        fetchRows<CrudRecord>('return_orders', 'date desc', { with_items: '1' }),
        fetchRows<CrudRecord>('reports/registers', 'created_at desc'),
        fetchRows<CrudRecord>('payments', 'created_at desc'),
        fetchRows<CrudRecord>('incomes', 'date desc'),
        fetchRows<CrudRecord>('expenses', 'date desc'),
        fetchDashboard().catch(() => null),
        fetchBusinessInsights().catch(() => null)
      ]);

    const rows = { sales: sales.rows, purchases: purchases.rows, items: items.rows, accounts: accounts.rows, customers: customers.rows, suppliers: suppliers.rows, orders: orders.rows, returnOrders: returnOrders.rows, registerRecords: registerRecords.rows, payments: payments.rows, incomes: incomes.rows, expenses: expenses.rows };
    const snapshot = buildBusinessSnapshot({ sales: sales.rows, returnOrders: returnOrders.rows, items: items.rows, registerRecords: registerRecords.rows });
    const serverMessages = normalizeServerMessages(serverInsights?.messages);

    setData({
      loading: false,
      error: null,
      rows,
      counts: { orders: orders.count, sales: sales.count, purchases: purchases.count, items: items.count, customers: customers.count, returnOrders: returnOrders.count },
      charts: {
        salesByRegister: buildSalesByRegister(sales.rows),
        salesByCashier: buildSalesByCashier(sales.rows),
        salesByPaymentType: buildSalesByPaymentType(sales.rows),
        salesByItem: buildItemsPie(sales.rows),
        salesByItemCategory: buildSalesByItemCategory(sales.rows),
        returnsByItem: buildItemsPie(returnOrders.rows),
        salesByHourLastSevenDays: buildSalesByHourLastSevenDays(sales.rows)
      },
      dashboard: dashboard as DashboardResponse | null,
      kpis: {
        revenueToday: revenueToday(sales.rows),
        revenueMonth: revenueThisMonth(sales.rows),
        cash: cashPosition(accounts.rows),
        inventory: inventoryValue(items.rows),
        receivables: receivables(sales.rows),
        payables: payables(purchases.rows),
        openCashRegisters: openCashRegisters(registerRecords.rows),
        ordersWaiting: ordersWaiting(orders.count),
        lowStock: lowStockKpi(items.rows)
      },
      alerts: serverMessages.length ? serverMessages : buildInsights(snapshot).map(translateBusinessMessage),
      snapshot,
      refreshedAt: new Date()
    });
  }

  // Location-scoped endpoints (sales, purchases, payments…) — refresh when the session location changes.
  useEffect(() => onLocationChanged(() => void load()), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void ensureLocation()
      .catch(() => undefined)
      .then(() => load());
    if (!autoRefresh) return;
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  if (!data) {
    return {
      loading: true,
      error: null,
      rows: { sales: [], purchases: [], items: [], accounts: [], customers: [], suppliers: [], orders: [], returnOrders: [], registerRecords: [], payments: [], incomes: [], expenses: [] },
      counts: { orders: 0, sales: 0, purchases: 0, items: 0, customers: 0, returnOrders: 0 },
      charts: { salesByRegister: [], salesByCashier: [], salesByPaymentType: [], salesByItem: [], salesByItemCategory: [], returnsByItem: [], salesByHourLastSevenDays: { categories: [], series: [] } },
      dashboard: null,
      kpis: { revenueToday: emptyKpi, revenueMonth: emptyKpi, cash: emptyKpi, inventory: emptyKpi, receivables: emptyKpi, payables: emptyKpi, openCashRegisters: emptyKpi, ordersWaiting: emptyKpi, lowStock: emptyKpi },
      alerts: [],
      snapshot: null,
      refreshedAt: null,
      refresh: load
    };
  }

  return { ...data, refresh: load };
}

const emptyKpi: Kpi = { label: '—', value: '—', tone: 'neutral' };
